// Task #2652 — Sezione Custom AI del master: selettore tenant + pannello
// condiviso `CustomAIPanel` (ui-frontend) iniettato col contesto master
// (apiClient master, basePath cross-tenant `/master/custom-ai/:tenantId`).
import { useEffect, useState } from 'react';
import { CustomAIPanel } from '@easyflow/ui-frontend';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@easyflow/ui-master/components/ui/select';
import { GlassCard, GlassCardContent } from '@easyflow/ui-master/components/ui/glass-card';
import { Loader2, Building2 } from 'lucide-react';
import apiClient from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import type { CrossTenantTenantOption } from '@easyflow/shared';

export default function CustomAIMasterSection() {
  const { toast } = useToast();
  const [tenants, setTenants] = useState<CrossTenantTenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await apiClient.getCrossTenantTenantOptions();
        if (!active) return;
        const options = (res.data ?? []).filter((t) => !t.is_master);
        setTenants(options);
        if (options.length > 0 && options[0]) setSelectedTenantId(options[0].id);
      } catch (err) {
        toast({
          title: 'Errore',
          description: err instanceof Error ? err.message : 'Impossibile caricare i tenant',
          variant: 'destructive',
        });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [toast]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento tenant…
      </div>
    );
  }

  if (tenants.length === 0) {
    return (
      <GlassCard>
        <GlassCardContent className="py-12 text-center text-muted-foreground">
          Nessun tenant disponibile.
        </GlassCardContent>
      </GlassCard>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Building2 className="h-5 w-5 text-muted-foreground" />
        <span className="text-sm font-medium">Tenant</span>
        <Select value={selectedTenantId} onValueChange={setSelectedTenantId}>
          <SelectTrigger className="w-[320px]">
            <SelectValue placeholder="Seleziona un tenant" />
          </SelectTrigger>
          <SelectContent>
            {tenants.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name} <span className="text-muted-foreground">({t.slug})</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedTenantId && (
        <CustomAIPanel
          key={selectedTenantId}
          deps={{
            api: apiClient,
            basePath: `/master/custom-ai/${selectedTenantId}`,
            notify: (input) => toast(input),
            formatError: (err, fb) => (err instanceof Error ? err.message : fb),
          }}
        />
      )}
    </div>
  );
}
